import React, { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import ExcelJS from 'exceljs';
import { Container, Card, Button, Alert, ListGroup, Form } from 'react-bootstrap';
import { saveAs } from 'file-saver';
import { Helmet } from 'react-helmet-async';
import ProcessingProgress from './common/ProcessingProgress';
import useProcessingProgress from '../hooks/useProcessingProgress';
import '../styles/components.css';

const ExcelDeduplication = () => {
  const [files, setFiles] = useState([]);
  const [error, setError] = useState('');
  const [previewData, setPreviewData] = useState(null);
  const [fileDetails, setFileDetails] = useState(null);
  const [selectedColumns, setSelectedColumns] = useState([]);
  const [deduplicationOptions, setDeduplicationOptions] = useState({
    keepFirst: true,
    caseSensitive: true,
    trimSpaces: true
  });
  const [processingSummary, setProcessingSummary] = useState(null);
  
  const {
    progress,
    status,
    isComplete,
    isLoading,
    simulateProgress,
    resetProgress
  } = useProcessingProgress();

  const deduplicationStages = [
    { progress: 0, status: 'Starting deduplication process...', duration: 500 },
    { progress: 20, status: 'Loading workbook...', duration: 800 },
    { progress: 40, status: 'Analyzing data...', duration: 1000 },
    { progress: 70, status: 'Removing duplicates...', duration: 1200 },
    { progress: 90, status: 'Preparing final workbook...', duration: 500 },
    { progress: 100, status: 'Complete!', duration: 500 }
  ];

  const formatCellValue = (value) => {
    if (value === null || value === undefined) return '';
    
    // Check if the value is a date
    if (value instanceof Date) {
      return value.toISOString().split('T')[0]; // Returns YYYY-MM-DD
    }
    
    // Handle Excel serial dates (which ExcelJS converts to JS Date objects)
    if (typeof value === 'object' && value.getTime && !isNaN(value.getTime())) {
      return value.toISOString().split('T')[0];
    }
    
    return value.toString();
  };

  const onDrop = useCallback(async (acceptedFiles) => {
    // Filter only Excel files
    const excelFiles = acceptedFiles.filter(file => 
      file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
      file.type === 'application/vnd.ms-excel' ||
      file.name.endsWith('.xlsx') ||
      file.name.endsWith('.xls')
    );

    if (excelFiles.length === 0) {
      setError('Please upload valid Excel files (.xlsx or .xls)');
      return;
    }

    if (excelFiles.length > 1) {
      setError('Please upload only one Excel file at a time');
      return;
    }

    setError('');
    setFiles(excelFiles);
    setProcessingSummary(null); // Clear processing summary
    resetProgress(); // Reset progress status
    
    // Load and preview the file
    try {
      const file = excelFiles[0];
      const workbook = new ExcelJS.Workbook();
      const arrayBuffer = await file.arrayBuffer();
      await workbook.xlsx.load(arrayBuffer);
      
      const worksheet = workbook.worksheets[0];
      
      // Get headers from first row, preserving empty values
      const headers = [...worksheet.getRow(1).values];
      // Remove the first element which is typically undefined in Excel
      headers.shift();
      
      // Get preview data (next 4 rows after header)
      const previewRows = [];
      let rowCount = 0;
      worksheet.eachRow((row, rowNumber) => {
        // Skip header row
        if (rowNumber > 1 && rowCount < 4) {
          // Create a new array with all values, including empty ones
          let rowValues = [...row.values];
          rowValues.shift();
          
          // Ensure the row has the same number of columns as headers
          while (rowValues.length < headers.length) {
            rowValues.push('');
          }
          
          // Format any date values
          rowValues = rowValues.map(value => formatCellValue(value));
          
          previewRows.push(rowValues);
          rowCount++;
        }
      });
      
      // Set file details
      setFileDetails({
        name: file.name,
        size: (file.size / 1024 / 1024).toFixed(2) + ' MB',
        rows: worksheet.rowCount - 1, // Subtract header row
        columns: headers.length
      });
      
      setPreviewData({
        headers,
        rows: previewRows
      });
      
      // Reset selected columns
      setSelectedColumns([]);
    } catch (err) {
      setError('Error reading Excel file: ' + err.message);
    }
  }, [resetProgress]);

  const checkForDuplicates = (rows, selectedColumns, options) => {
    const keyMap = new Map();
    
    for (const row of rows) {
      const key = selectedColumns.map(colIndex => {
        let value = row[colIndex];
        if (typeof value === 'string') {
          if (options.trimSpaces) {
            value = value.trim();
          }
          if (!options.caseSensitive) {
            value = value.toLowerCase();
          }
        }
        return value;
      }).join('|');

      if (keyMap.has(key)) {
        return true; // Found a duplicate
      }
      keyMap.set(key, true);
    }
    
    return false; // No duplicates found
  };

  const handleDeduplication = async () => {
    if (files.length === 0 || selectedColumns.length === 0) {
      setError('Please select a file and at least one column for deduplication');
      return;
    }

    try {
      const file = files[0];
      const workbook = new ExcelJS.Workbook();
      const arrayBuffer = await file.arrayBuffer();
      await workbook.xlsx.load(arrayBuffer);
      
      const worksheet = workbook.worksheets[0];
      const headers = worksheet.getRow(1).values;
      headers.shift();
      
      // Get all data rows first
      const rows = [];
      worksheet.eachRow((row, rowNumber) => {
        if (rowNumber > 1) { 
          let rowValues = [...row.values];
          rowValues.shift();
          rows.push(rowValues);
        }
      });

      // Check for duplicates before proceeding
      const hasDuplicates = checkForDuplicates(rows, selectedColumns, deduplicationOptions);
      
      if (!hasDuplicates) {
        setError('No duplicates found in the selected columns.');
        return;
      }

      // Clear any previous error
      setError('');
      
      // Start progress bar and processing
      await simulateProgress(deduplicationStages);
      
      // Create unique key for each row based on selected columns
      const uniqueMap = new Map();
      const duplicateTracker = new Map(); // Track all rows with same key
      
      rows.forEach((row, index) => {
        const key = selectedColumns.map(colIndex => {
          let value = row[colIndex];
          if (typeof value === 'string') {
            if (deduplicationOptions.trimSpaces) {
              value = value.trim();
            }
            if (!deduplicationOptions.caseSensitive) {
              value = value.toLowerCase();
            }
          }
          return value;
        }).join('|');

        // Track all rows with this key
        if (!duplicateTracker.has(key)) {
          duplicateTracker.set(key, []);
        }
        duplicateTracker.get(key).push({ row, index });
        
        if (deduplicationOptions.keepFirst) {
          if (!uniqueMap.has(key)) {
            uniqueMap.set(key, row);
          }
        } else {
          // For keep last, always update
          uniqueMap.set(key, row);
        }
      });
      
      // Create new workbook with deduplicated data
      const newWorkbook = new ExcelJS.Workbook();
      const newWorksheet = newWorkbook.addWorksheet('Deduplicated');
      const duplicatesWorksheet = newWorkbook.addWorksheet('Removed Duplicates');
      
      // Add headers to both sheets
      [newWorksheet, duplicatesWorksheet].forEach(sheet => {
        const headerRow = sheet.addRow(headers);
        headerRow.font = { bold: true };
      });
      
      // Add unique rows to main sheet
      const uniqueRows = Array.from(uniqueMap.values());
      uniqueRows.forEach(row => {
        newWorksheet.addRow(row);
      });
      
      // Add duplicate rows to second sheet
      const duplicateRows = [];
      duplicateTracker.forEach((rowsWithKey, key) => {
        if (rowsWithKey.length > 1) {
          // If keeping first, all but first are duplicates
          if (deduplicationOptions.keepFirst) {
            duplicateRows.push(...rowsWithKey.slice(1).map(r => r.row));
          } else {
            // If keeping last, all but last are duplicates
            duplicateRows.push(...rowsWithKey.slice(0, -1).map(r => r.row));
          }
        }
      });
      
      duplicateRows.forEach(row => {
        duplicatesWorksheet.addRow(row);
      });
      
      // Generate and save the file
      const buffer = await newWorkbook.xlsx.writeBuffer();
      const originalName = file.name.split('.')[0];
      const extension = file.name.split('.').pop();
      const newFilename = `${originalName}_dedupe.${extension}`;
      saveAs(new Blob([buffer]), newFilename);
      
      // Set summary
      setProcessingSummary({
        totalRows: rows.length,
        removedRows: duplicateRows.length,
        uniqueRows: uniqueRows.length
      });
      
    } catch (err) {
      setError('Error processing file: ' + err.message);
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-excel': ['.xls']
    }
  });

  return (
    <>
      <Helmet>
        <title>Excel Deduplication - Remove Duplicate Records | Excel Toolbox</title>
        <meta name="description" content="Free online Excel deduplication tool. Remove duplicate records from your Excel files quickly and easily. Process files securely on your device with no data upload." />
        <meta name="keywords" content="excel deduplication, remove duplicates excel, excel duplicate remover, excel data cleaner" />
      </Helmet>

      <Container className="tool-container">
        <Card className="tool-card">
          <Card.Header as="h5" className="tool-header">Excel Deduplication</Card.Header>
          <Card.Body className="tool-body">
            <Card.Text className="mb-4">
              Remove duplicate records from your Excel files quickly and easily. Simply load your Excel file below,
              select the column(s) used to check for duplicates, and download the cleaned result.  Any removed duplicate records will be saved in a separate sheet.
            </Card.Text>

            {error && <Alert variant="danger" className="mb-4">{error}</Alert>}

            <div className="dropzone-container">
              <div {...getRootProps()}>
                <input {...getInputProps()} />
                <div className={`dropzone ${isDragActive ? 'active' : ''}`}>
                  <i className="bi bi-cloud-upload dropzone-icon"></i>
                  <p className="dropzone-text">
                    {isDragActive ? 'Drop the files here' : 'Drag and drop Excel files here, or click to select'}
                  </p>
                </div>
              </div>
            </div>

            {fileDetails && (
              <Card className="mt-4">
                <Card.Body>
                  <h6>File Details:</h6>
                  <ListGroup variant="flush">
                    <ListGroup.Item>Name: {fileDetails.name}</ListGroup.Item>
                    <ListGroup.Item>Size: {fileDetails.size}</ListGroup.Item>
                    <ListGroup.Item>Rows: {fileDetails.rows}</ListGroup.Item>
                    <ListGroup.Item>Columns: {fileDetails.columns}</ListGroup.Item>
                  </ListGroup>
                </Card.Body>
              </Card>
            )}

            {previewData && (
              <>
                <Card className="mt-4">
                  <Card.Body>
                    <h6>Preview:</h6>
                    <div className="table-responsive" style={{ maxHeight: '300px' }}>
                      <table className="table table-sm table-bordered">
                        <thead style={{ backgroundColor: '#198754', color: 'white', position: 'sticky', top: 0 }}>
                          <tr>
                            {previewData.headers.map((header, index) => (
                              <th key={index} style={{ 
                                whiteSpace: 'nowrap', 
                                minWidth: '150px',
                                backgroundColor: selectedColumns.includes(index) ? '#146c43' : '#198754',
                                color: 'white'
                              }}>
                                <Form.Check
                                  type="checkbox"
                                  label={header}
                                  checked={selectedColumns.includes(index)}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      setSelectedColumns([...selectedColumns, index]);
                                    } else {
                                      setSelectedColumns(selectedColumns.filter(i => i !== index));
                                    }
                                  }}
                                  style={{
                                    color: 'white'
                                  }}
                                />
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {previewData.rows.map((row, rowIndex) => (
                            <tr key={rowIndex}>
                              {row.map((cell, cellIndex) => (
                                <td 
                                  key={cellIndex} 
                                  style={{ 
                                    whiteSpace: 'nowrap', 
                                    overflow: 'hidden', 
                                    textOverflow: 'ellipsis',
                                    backgroundColor: selectedColumns.includes(cellIndex) ? '#f8f9fa' : 'transparent'
                                  }}
                                >
                                  {formatCellValue(cell)}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </Card.Body>
                </Card>

                <Card className="mt-4">
                  <Card.Body>
                    <h6>Deduplication Options:</h6>
                    <Form>
                      <Form.Group className="mb-3">
                        <Form.Check
                          type="radio"
                          name="duplicateOption"
                          label="Keep first occurrence"
                          checked={deduplicationOptions.keepFirst}
                          onChange={() => setDeduplicationOptions({...deduplicationOptions, keepFirst: true})}
                        />
                        <Form.Check
                          type="radio"
                          name="duplicateOption"
                          label="Keep last occurrence"
                          checked={!deduplicationOptions.keepFirst}
                          onChange={() => setDeduplicationOptions({...deduplicationOptions, keepFirst: false})}
                        />
                      </Form.Group>
                      <Form.Group className="mb-3">
                        <Form.Check
                          type="checkbox"
                          label="Case-sensitive matching"
                          checked={deduplicationOptions.caseSensitive}
                          onChange={(e) => setDeduplicationOptions({...deduplicationOptions, caseSensitive: e.target.checked})}
                        />
                      </Form.Group>
                      <Form.Group className="mb-3">
                        <Form.Check
                          type="checkbox"
                          label="Ignore leading/trailing spaces"
                          checked={deduplicationOptions.trimSpaces}
                          onChange={(e) => setDeduplicationOptions({...deduplicationOptions, trimSpaces: e.target.checked})}
                        />
                      </Form.Group>
                    </Form>
                    
                    <Button 
                      variant="success"
                      onClick={handleDeduplication}
                      disabled={isLoading || selectedColumns.length === 0}
                      className="action-button"
                    >
                      {isLoading ? 'Removing Duplicates...' : 'Check & Remove Duplicates'}
                    </Button>
                  </Card.Body>
                </Card>
              </>
            )}

            <div className="mt-4">
              <ProcessingProgress 
                progress={progress}
                status={status}
                isComplete={isComplete}
                isLoading={isLoading}
                completionMessage="Deduplication completed successfully! Your download should start automatically."
              />

              {processingSummary && (
                <Alert variant="success" className="mt-4">
                  Successfully processed file!
                  <ul className="mb-0">
                    <li>Total rows processed: {processingSummary.totalRows}</li>
                    <li>Duplicate rows removed: {processingSummary.removedRows}</li>
                    <li>Unique rows remaining: {processingSummary.uniqueRows}</li>
                  </ul>
                </Alert>
              )}
            </div>
          </Card.Body>
        </Card>
      </Container>
    </>
  );
};

export default ExcelDeduplication;
